import { yupResolver } from "@hookform/resolvers/yup";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";

import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";

import Copyright from "../../../components/Copyright";

import { useSetPasswordMutation } from "../authApiSlice";

import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import LoginBackground from "../../../components/LoginBackground";
import Logo from "../../../components/Logo";
import ControlledTextField from "../../../components/controlled/ControlledTextField";
import { getFormSchema } from "./formSchema";

export default function SetPassword() {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const [setPassword, { isLoading }] = useSetPasswordMutation();

	const formSchema = getFormSchema(t);
	const {
		handleSubmit,
		control,
		formState: { errors },
		setError,
	} = useForm({
		resolver: yupResolver(formSchema),
		shouldFocusError: true,
	});

	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	const onSubmit = async ({ password, passwordConfirm }) => {
		await setPassword({
			token,
			data: { password, passwordConfirm },
		})
			.unwrap()
			.then((_payload) => {
				enqueueSnackbar(t("password_has_been_changed"), {
					variant: "success",
					preventDuplicate: true,
				});
				setTimeout(() => {
					navigate("/login");
				}, 500);
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				if (err.message) {
					enqueueSnackbar(err.message, {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
		}
	}, [errors, enqueueSnackbar]);

	return (
		<Grid container component="main" sx={{ height: "100vh" }}>
			<CssBaseline />
			<LoginBackground>
				<Box>
					<Box sx={{ display: "flex", justifyContent: "center", mb: 5 }}>
						<Logo />
					</Box>
					<Box
						component="form"
						noValidate
						onSubmit={handleSubmit(onSubmit)}
						sx={{
							pt: 5,
							pb: 7,
							px: 6,
							borderRadius: 0,
							background: (theme) => theme.palette.loginBox,
							width: "442px",
						}}
					>
						<ControlledTextField
							sx={{ "& .MuiInputBase-root": { background: "#fff !important" } }}
							shrinkLabel={false}
							variant="filled"
							margin="normal"
							control={control}
							name="password"
							label={t("password_label")}
							error={!!errors?.password}
							defaultValue=""
							type="password"
							autoComplete="password"
							required
						/>
						<ControlledTextField
							sx={{ "& .MuiInputBase-root": { background: "#fff !important" } }}
							shrinkLabel={false}
							variant="filled"
							margin="normal"
							control={control}
							name="passwordConfirm"
							label={t("confirm_password_label")}
							error={!!errors?.passwordConfirm}
							defaultValue=""
							type="password"
							autoComplete="password"
							required
						/>
						<Grid container>
							<Grid size="grow">
								<Link to="/login" style={{ color: "#fff" }}>
									{t("proceed_to_login")}
								</Link>
							</Grid>
						</Grid>
						<Box sx={{ display: "flex", justifyContent: "center" }}>
							<Button
								loading={isLoading}
								type="submit"
								variant="contained"
								sx={{ mt: 3 }}
							>
								{t("set_password")}
							</Button>
						</Box>
					</Box>
				</Box>
				<Copyright sx={{ mt: 8, mb: 4 }} />
			</LoginBackground>
		</Grid>
	);
}
